<template>
  <div :class="controlClasses" class="mds-form-control mds-input-percentage">
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <!-- Верх: кнопки, иконка, input, unit, очистка -->
      <div class="control-row">
        <input
          :disabled="disabled"
          :id="uid"
          :ref="uid"
          :placeholder="placeholder"
          :value="localValue"
          type="text"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />

        <span class="unit">%</span>

        <button @click="onClear" class="clear" type="button" v-if="showClear">
          <VIcon material="cancel" />
        </button>
      </div>

      <div class="messages-row" v-if="isErrorsVisible">
        <div class="error" v-if="validation.isValid === false">
          <div
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
            class="error-message"
          >
            {{ error }}
          </div>
        </div>

        <div class="warning" v-if="validation.warnings.length > 0">
          <div
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
            class="warning-message"
          >
            {{ warning }}
          </div>
        </div>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>
<!--<style lang="scss" src="./VInputPercentage.scss" />-->

<script lang="ts">
import Core         from "@/core/Core";
import VBaseControl from "@/core/services/VBaseControl";
import Validation   from "@/core/services/Validation";
import Multiplier   from "@/core/types/Multiplier";

/**
 * VInputPercentage — числовой ввод процента для Multiplier.
 *
 * Отдельный компонент, а не режим VInputNumberRusV2, потому что у Multiplier
 * внешнее представление (процент) и внутреннее (множитель, 1 + отклонение)
 * математически различны — VInputNumberRusV2._buildValue строит значение
 * через голый конструктор (new this.type(str)), что для Multiplier создаёт
 * множитель из введённого числа напрямую, а не процент через fromPercent.
 * Ввод "1" (подразумевая 1%) давал множитель 1.0 (= 0%), ввод "2" — множитель
 * 2.0 (= 100%). Единственный Numeric-наследник с такой асимметрией — отдельный
 * компонент проще и явнее, чем городить универсальный fromDisplayValue
 * ради одного случая.
 *
 * v-model принимает и отдаёт Multiplier|null. Пользователь печатает процент
 * ("22", "-10"), наружу уходит Multiplier (1.22, 0.90).
 */
export default defineComponent({
  name: 'VInputPercentage',

  extends: VBaseControl,

  props: {
    /** Значение. Multiplier или null (пустое поле — штатно). */
    modelValue: { type: Object as PropType<Multiplier | null>, default: null },
    /** Показывать кнопку очистки. */
    clearable:  { type: Boolean, default: false },
    /** Границы ВВОДИМОГО процента (не внутреннего множителя). */
    min:        { type: Number, default: null },
    max:        { type: Number, default: null },
  },

  emits: ['update:modelValue', 'input', 'blur', 'focus'],

  data() {
    const validation = Validation.fromProps(this as any);

    if (this.min !== null) validation.min(this.min);
    if (this.max !== null) validation.max(this.max);

    return {
      validation,
      localValue:         null as string | null,

      // Кэш внешнего значения для разведения «строки в поле» и «значения модели».
      // Храним toJSON() Multiplier — это ПРОЦЕНТ (не внутренний множитель)
      externalValueCache: null as string | null,
    };
  },

  computed: {
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true) classes.push('clearable');
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.localValue !== null
        && this.localValue.length > 0
        && this.disabled === false;
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) {
        result['width'] = `calc(100% - ${this.labelWidth})`;
      }
      return result;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots['helper-text'] !== undefined;
    },

    /** Правила валидации — границы вводимого процента (min/max из пропов). */
    getValidateRules() {
      return {
        min: this.min,
        max: this.max,
      };
    },
  },

  watch: {
    modelValue(newValue, oldValue) {
      if (newValue === oldValue) return;
      this._syncLocalValue();
    },
  },

  created() {
    this._validateModelType();
    this._syncLocalValue(true);
  },

  methods: {
    _validateModelType() {
      if (this.modelValue !== null && !(this.modelValue instanceof Multiplier)) {
        Core.error('VInputPercentage: modelValue не является экземпляром Multiplier');
      }
    },

    onInput(event) {
      this.localValue = this._formatPercentString(event.target.value);
      event.target.value = this.localValue;

      const value = this._buildValue(this.localValue);

      if (this.validation.isDirty === false) {
        const prev = this.externalValueCache;
        const next = value !== null ? value.toJSON() : null;
        if (prev !== next) {
          this.validation.markDirty();
        }
      }

      this.externalValueCache = value !== null ? value.toJSON() : null;
      this._emitValue(value);
    },

    async onBlur(event) {
      if (typeof this.debounceValidation.cancel === 'function') {
        this.debounceValidation.cancel();
      }

      await this.validate();

      const value = this._buildValue(event.target.value);
      this.localValue = value !== null ? value.asPercent().toFixed(2) : null;
      this.externalValueCache = value !== null ? value.toJSON() : null;
      event.target.value = this.localValue ?? '';
      this.$emit('blur', value);
    },

    onFocus(event) {
      this.validation.markTouched();
      this.$emit('focus', event.target);
    },

    _emitValue(value: Multiplier | null) {
      this.$emit('update:modelValue', value);
      this.$emit('input', value);
    },

    /**
     * Синхронизирует строку в поле с внешним значением. modelValue уже
     * Multiplier — берём процент через asPercent, не toJSON, чтобы поле
     * показывало то же представление, что печатает пользователь.
     */
    _syncLocalValue(force = false) {
      const external = this.modelValue;
      const externalKey = external !== null ? external.toJSON() : null;

      if (force === false && externalKey === this.externalValueCache) return;

      this.externalValueCache = externalKey;
      this.localValue = external !== null
        ? this._formatPercentString(external.asPercent().toFixed(2))
        : null;

      if (force === false) {
        this.debounceValidation();
      }
    },

    /**
     * Собирает Multiplier из введённого процента через fromPercent —
     * симметрично asPercent, которым построена _syncLocalValue.
     */
    _buildValue(stringValue: string | null): Multiplier | null {
      if (stringValue === null || stringValue.length === 0) {
        return null;
      }
      const parsed = parseFloat(stringValue);
      if (isNaN(parsed)) {
        return null;
      }
      return Multiplier.fromPercent(stringValue.replace(/\.$/, ''));
    },

    onClear() {
      this.localValue = null;
      this.externalValueCache = null;
      this._emitValue(null);
      this.$emit('blur', null);
    },

    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this._buildValue(this.localValue));
    },

    /**
     * Нормализует ввод процента: цифры, точка/запятая→точка, минус, 2 знака
     * после запятой (SCALE Multiplier эффективно даёт 2 знака процента).
     * Урезанная версия _formatNumberString из VInputNumberRusV2 — процент
     * не имеет собственной "разрядности целой части" сверх обычных границ.
     */
    _formatPercentString(value: string | null): string | null {
      if (typeof value !== 'string' || value.length === 0) {
        return value;
      }

      let minus = '';
      let _value = value;
      if (_value[0] === '-') {
        minus = '-';
        _value = _value.slice(1);
      }

      _value = _value.replace(/[^\d,.]+/, '').replace(',', '.');

      const dotsArray = [..._value.matchAll(/\./g)];
      if (dotsArray.length === 2) {
        const _valueArray = _value.split('');
        _valueArray.splice(dotsArray[1].index, 1);
        _value = _valueArray.join('');
      }

      const [intPart, decPart] = _value.split('.');
      if (decPart !== undefined) {
        _value = `${intPart}.${decPart.slice(0, 2)}`;
      }

      // Если min >= 0 — запрещаем минус целиком, знак не нужен вообще
      const rules = this.getValidateRules;
      if (rules.min !== null && rules.min >= 0) {
        minus = '';
      }

      return minus + _value;
    },
  },
});
</script>
